import { createStore } from "zustand/vanilla";
import type { AuthStatus } from "types/auth";
import type { AuthUser, DevicePorts } from "./ports";
import { waitForAuthReady } from "../utils/auth/waitForAuthReady";

export function createAuthSession(adapter: DevicePorts["auth"]) {
  const controller = new AbortController();
  const subscriptions = new Set<() => void>();
  const store = createStore<{
    status: AuthStatus;
    firebaseUser: AuthUser | null | undefined;
    setStatus(status: AuthStatus): void;
  }>((set) => ({
    status: "firebase-auth-pending",
    firebaseUser: undefined,
    setStatus: (status) => set({ status }),
  }));
  const session = {
    ...adapter,
    subscribe(listener: Parameters<DevicePorts["auth"]["subscribe"]>[0]) {
      if (controller.signal.aborted) throw new Error("Auth session has ended");
      const stop = adapter.subscribe(listener);
      const unsubscribe = () => {
        if (!subscriptions.delete(unsubscribe)) return;
        stop();
      };
      subscriptions.add(unsubscribe);
      return unsubscribe;
    },
    store,
    signal: controller.signal,
    waitForReady: (timeoutMs?: number) => waitForAuthReady(session, timeoutMs),
    dispose() {
      controller.abort();
      for (const stop of subscriptions) stop();
    },
  };
  return session;
}

export type AuthSession = ReturnType<typeof createAuthSession>;
